import { useState } from 'react';
import { Card, Descriptions, Grid, Modal, Table, Tag, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ParticipantReferralItem } from '../../types/engagement.types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface EngagementReferralTableProps {
  referrals: ParticipantReferralItem[];
  loading?: boolean;
}

const statusColor: Record<string, string> = {
  PENDING: 'processing',
  QUALIFIED: 'success',
  NOT_QUALIFIED: 'default',
  DISCARDED: 'error',
};

const statusLabel: Record<string, string> = {
  PENDING: 'Pendente',
  QUALIFIED: 'Qualificada',
  NOT_QUALIFIED: 'Não qualificada',
  DISCARDED: 'Desconsiderada',
};

function renderDetalhe(item: ParticipantReferralItem) {
  if (item.discardedReason) return <Text type="danger">{item.discardedReason}</Text>;
  if (item.qualifiedAt) return <Text type="success">Qualificada em {dayjs(item.qualifiedAt).format('DD/MM/YYYY')}</Text>;
  return <Text type="secondary">—</Text>;
}

export const EngagementReferralTable = ({ referrals, loading = false }: EngagementReferralTableProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [selected, setSelected] = useState<ParticipantReferralItem | null>(null);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
      responsive: ['sm'] as ('sm')[],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      render: (value: string) => <Tag color={statusColor[value] || 'default'}>{statusLabel[value] || value}</Tag>,
    },
    {
      title: 'Cadastro',
      dataIndex: 'referredRegisteredAt',
      responsive: ['sm'] as ('sm')[],
      render: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Prazo de qualificação',
      dataIndex: 'qualificationDeadlineAt',
      render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
    },
    ...(!isMobile ? [{
      title: 'Detalhe',
      render: (_: unknown, item: ParticipantReferralItem) => renderDetalhe(item),
    }] : []),
    ...(isMobile ? [{
      key: 'info',
      width: 48,
      render: (_: unknown, item: ParticipantReferralItem) => (
        <InfoCircleOutlined
          style={{ fontSize: 18, color: '#1677ff', cursor: 'pointer' }}
          onClick={() => setSelected(item)}
        />
      ),
    }] : []),
  ];

  return (
    <>
      <Card title="Status das suas indicações" style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'Nenhuma indicação registrada.' }}
          dataSource={referrals}
          scroll={{ x: 'max-content' }}
          columns={columns}
        />
      </Card>

      <Modal
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={null}
        title={`Indicação #${selected?.id}`}
        centered
      >
        {selected && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{selected.id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColor[selected.status] || 'default'}>
                {statusLabel[selected.status] || selected.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Cadastro">
              {selected.referredRegisteredAt
                ? dayjs(selected.referredRegisteredAt).format('DD/MM/YYYY HH:mm')
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Prazo de qualificação">
              {dayjs(selected.qualificationDeadlineAt).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Detalhe">
              {renderDetalhe(selected)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default EngagementReferralTable;
